import { IUser } from '@interfaces/user';
import { getEnvConfig } from 'getEnvConfig';
import jwt_decode from 'jwt-decode';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authProvider } from '../../authProvider';

/**
 * Coco, the signed-in AI assistant: the `agent-portal` parcel (agent-web) mounted in its own Shadow DOM, so its React
 * and styles never touch this app. The parcel talks to agent-api with the portal JWT (kept in its memory only).
 *
 * Env: AGENT_API_URL (agent-api) and AGENT_PARCEL_URL (CDN folder with agent-portal.umd.js). Without both, or without a
 * signed-in user, nothing is rendered. Rendered next to the app, outside the router, so the route comes from the URL.
 */

type AgentPortalGlobal = {
    mount: (container: HTMLElement, config: unknown) => void;
    update: (container: HTMLElement, config: unknown) => void;
    unmount: (container: HTMLElement) => void;
};

const parcelOf = () => (window as unknown as { AgentPortal?: AgentPortalGlobal }).AgentPortal;

const scripts = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
    let loading = scripts.get(src);
    if (!loading) {
        loading = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scripts.delete(src);
                reject(new Error(`Could not load ${src}`));
            };
            document.head.appendChild(script);
        });
        scripts.set(src, loading);
    }
    return loading;
}

/** `location.pathname + search`, kept current across pushState / replaceState / back and forward. */
function useLocationKey(): string {
    const [key, setKey] = useState(() => window.location.pathname + window.location.search);
    useEffect(() => {
        const update = () => setKey(window.location.pathname + window.location.search);
        const { pushState, replaceState } = window.history;
        window.history.pushState = function (...args) {
            const result = pushState.apply(this, args);
            update();
            return result;
        };
        window.history.replaceState = function (...args) {
            const result = replaceState.apply(this, args);
            update();
            return result;
        };
        window.addEventListener('popstate', update);
        return () => {
            window.history.pushState = pushState;
            window.history.replaceState = replaceState;
            window.removeEventListener('popstate', update);
        };
    }, []);
    return key;
}

const identityOf = (token: string): IUser.IUserIdentity | null => {
    try {
        return jwt_decode<IUser.IUserIdentity>(token);
    } catch {
        return null;
    }
};

export const AgentAssistant: FC = () => {
    const container = useRef<HTMLDivElement>(null);
    const { i18n } = useTranslation();
    const location = useLocationKey();
    const apiUrl = getEnvConfig.AGENT_API_URL;
    const parcelUrl = getEnvConfig.AGENT_PARCEL_URL;
    // re-read on every navigation: the user signs in and out, and the token is renewed, without this component remounting
    const token = authProvider.getToken() ?? '';
    const user = token ? identityOf(token) : null;
    const signedIn = !!user?.id && !!user?.tenantId;

    const config = () => {
        const [pathname, search] = location.split('?');
        const current = authProvider.getToken() ?? token;
        const identity = identityOf(current) ?? user;
        return {
            mode: 'portal',
            lang: (i18n?.language || 'en').slice(0, 2),
            env: { AGENT_API_URL: apiUrl },
            auth: { token: current },
            context: {
                tenant: identity?.tenantId ? { id: identity.tenantId } : null,
                user: {
                    id: identity?.id,
                    name:
                        [identity?.firstName, identity?.lastName].filter(Boolean).join(' ') ||
                        identity?.name ||
                        undefined,
                    email: identity?.email,
                    avatar: identity?.avatar,
                },
                module: { key: 'portal-web', title: 'Nuvioo Portal' },
                route: {
                    pathname,
                    search: search ? `?${search}` : undefined,
                    title: document.title,
                },
            },
            // the host renews the token itself (core-ui's http client): hand the newest one back, if there is one
            onUnauthorized: async () => {
                const latest = authProvider.getToken();
                return latest && latest !== current ? latest : undefined;
            },
        };
    };

    useEffect(() => {
        const element = container.current;
        if (!element || !apiUrl || !parcelUrl || !signedIn) return;
        let mounted = false;
        let cancelled = false;
        loadScript(`${parcelUrl.replace(/\/$/, '')}/agent-portal.umd.js`)
            .then(() => {
                const parcel = parcelOf();
                if (cancelled || !parcel) return;
                parcel.mount(element, config());
                mounted = true;
            })
            .catch((error) => console.warn('[Coco]', error));
        return () => {
            cancelled = true;
            const parcel = parcelOf();
            if (mounted && parcel) parcel.unmount(element);
        };
        // mount once per sign-in; changes are pushed below
    }, [apiUrl, parcelUrl, signedIn, user?.id, user?.tenantId]);

    // route, language or a renewed token: Coco follows from the next message
    useEffect(() => {
        const element = container.current;
        const parcel = parcelOf();
        if (element && parcel && signedIn) parcel.update(element, config());
    }, [location, i18n?.language, token]);

    if (!apiUrl || !parcelUrl || !signedIn) return null;
    return <div ref={container} data-coco-assistant="" />;
};
